/** @jsxImportSource @emotion/react */
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Suspense,
  Fragment,
  useState,
  MouseEvent,
  lazy,
  useEffect,
  useCallback,
  useMemo,
  Dispatch,
  SetStateAction,
} from 'react';
import ConfirmationModal from '../../../../../shared/components/atoms/confirmation-modal/index';
import QSModal from '../../../../../shared/components/molecules/modal';
import { Box } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import deleteChatsUseCase from '../../use-cases/delete-chats.use-case';
import {
  selectedChatIdsListAtom,
  toggleSelectedChatIdsAtom,
  isAllChatsSelectedAtom,
  excludedChatIdsAtom,
} from '../../states/chat-list';
import { chatListFamily } from '../../states/chat';
import { AddTagAssignType, ConfirmationModalStateProps, PopupName } from '../../typings/chat';
import { assignMultipleChatsToUserUseCase, unassignMultipleChatsUseCase } from '../../use-cases/assign-user.use-case';
import { buildBulkActionFilter } from '../../requests/bulk-action';
import { useBulkActionOnChats } from '../../hooks/use-bulk-action-on-chats';
import { chatListAtom } from '../../states/chat-list';
import Loader from '../../../../../shared/components/molecules/loader';
import { LOADING, DEFAULT_TEAM } from '../../../../../shared/constants';
import useNotifications from '../../../../../shared/hooks/use-notifications';
import { css } from '@emotion/react';
import { postMessageToIframe } from '../../../../../shared/utils/post-message';
import { useAtomCallback } from 'jotai/utils';
import { buttonBgColor } from '../../../../../shared/typings';
import { WabaChannelType } from '../../../../../shared/typings/waba';
import { wabaIntegrationsAtomFamily } from '../../../../../shared/states/waba';
import { ALL_CHANNELS } from '../../../../../shared/constants';
import { ChatProviderType, ChatType } from '../chat-list-section/chat-row/typings';
import { useCustomNavigate } from '../../../../../shared/hooks/use-conversation-navigate';
import { useUserPermissions } from '../../../../../shared/hooks/use-permission';
import CustomTagsIcon from '../../../../../shared/icons/icons-revamp/custom_tags';
import UserIcon from '../../../../../shared/icons/icons-revamp/user';
import BroadcastIcon from '../../../../../shared/icons/icons-revamp/broadcast';
import UnassignAgent from '../../../../../shared/icons/icons-revamp/unassign-agent';
import DeleteIcon from '../../../../../shared/icons/icons-revamp/delete';
import { useSelectedChatsIncludeTypes } from '../../hooks/use-selected-chats-types';
import { useSelectedChatsIncludeProviderTypes } from '../../hooks/use-selected-provider-types';
import QSTooltip from '../../../../../shared/components/molecules/tooltip';
import { teamMembersAtom } from '../../states/team-members';
import { AgentStatus } from '../../../../agent-availability/typings';
import { customFieldsAtom } from '../../states/custom-fields';
import { BulkChatAction } from '../../typings/bulk-action-progress';

const AddTagLazyComponent = lazy(() => import('../conversation-tags/add-tag/index'));
const CreateTagLazyComponent = lazy(() => import('../conversation-tags/create-tag/index'));
const AssignModalLazyComponent = lazy(() => import('../assign-modal'));
const MemberChannelsManagementLazyComponent = lazy(
  () => import('../../../../crm-mini/channel-management/v1/MemberChannels')
);

const menuItemStyles = css`
  display: flex;
  height: 40px;
  padding: 6px 12px;
  align-items: center;
  gap: 6px;
  border-bottom: 1px solid #e0e0e9;
  font-size: 14px;
  font-weight: 500;
  color: #2d2d2d;
  &:hover {
    background-color: #f5f5f5;
  }
`;

const MultiSelectChatDropdown = ({
  selectedUserId,
  setSelectedUserId,
}: {
  selectedUserId: string;
  setSelectedUserId: Dispatch<SetStateAction<string>>;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  const { canDeleteConversation, canChangeChatAssignee, canManageChannels, canManageCreatedChannelAccess } =
    useUserPermissions();

  const [createTagPopup, setCreateTagPopup] = useState<boolean>(false);
  const [popupState, setPopupState] = useState<PopupName | null>(null);
  const { addNotification } = useNotifications();
  const { data: teamMembers } = useAtomValue(teamMembersAtom);
  const [selectedChatIdsList, setSelectedChatIdsList] = useAtom(selectedChatIdsListAtom);
  const setToggleSelectedChatIds = useSetAtom(toggleSelectedChatIdsAtom);
  const [isAllChatsSelected, setIsAllChatsSelected] = useAtom(isAllChatsSelectedAtom);
  const [excludedChatIds, setExcludedChatIds] = useAtom(excludedChatIdsAtom);
  const chatFilters = useAtomValue(chatListAtom);
  const navigate = useCustomNavigate();
  const isGroupView = chatFilters.conversationView === 'groups';

  const resetSelectionState = useCallback(() => {
    setIsAllChatsSelected(false);
    setExcludedChatIds([]);
  }, [setIsAllChatsSelected, setExcludedChatIds]);

  const getSelectedCustomerIds = useAtomCallback(
    useCallback(
      (get, set) => {
        return selectedChatIdsList.map((chatID) => get(chatListFamily({ id: chatID }))?.chatTypeId);
      },
      [selectedChatIdsList]
    )
  );

  const { data: customFieldsData } = useAtomValue(customFieldsAtom);
  const bulkAction = useBulkActionOnChats();

  const wabaIntegrationsAtom = useMemo(() => wabaIntegrationsAtomFamily({ id: ALL_CHANNELS }), []);
  const { data: allWabas = [] } = useAtomValue(wabaIntegrationsAtom);
  const orgHasInstagram = allWabas.some((w) => w.type === WabaChannelType.INSTAGRAM);

  const selectedChatsIncludeGroupAndWaGroup = useSelectedChatsIncludeTypes([ChatType.GROUP, ChatType.WA_GROUP]);
  const selectedChatsIncludeInstagram = useSelectedChatsIncludeProviderTypes([ChatProviderType.INSTAGRAM]);
  const selectedChatsIncludeGroup = useSelectedChatsIncludeTypes([ChatType.GROUP]);

  const showBroadcastItem =
    (canManageChannels.hasAccess || canManageCreatedChannelAccess.hasAccess) &&
    ((isAllChatsSelected && !isGroupView) ||
      (!selectedChatsIncludeGroupAndWaGroup() && !selectedChatsIncludeInstagram()));

  // Hide the three-dots button entirely when no menu items would be visible.
  const hasVisibleItems =
    !selectedChatsIncludeGroup() || showBroadcastItem || (!!canDeleteConversation?.hasAccess && !isAllChatsSelected);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setOpen((prev) => !prev);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setOpen((prev) => !prev);
    setAnchorEl(null);
  };

  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalStateProps>({
    open: false,
    message: '',
    onClick: async () => {},
    onClose: () => {},
    okButtonText: '',
    buttonBgColor: buttonBgColor.SUCCESS_BG_COLOR,
  });

  const handleOnClickDeleteChats = async () => {
    try {
      await deleteChatsUseCase(selectedChatIdsList);
      navigate({ pathname: '/conversations' }, { replace: true });
      addNotification({
        type: 'success',
        message: 'Deleted chats successfully',
      });
    } catch (error) {
      addNotification({
        type: 'error',
        message: (error as Error).message || 'Error while deleting chats',
      });
    } finally {
      setSelectedChatIdsList([]);
      setPopupState(null);
      setOpen(false);
      setToggleSelectedChatIds(false);
      resetSelectionState();
    }
  };

  const handleOnClickAssignUser = async () => {
    const selectedAgent = teamMembers?.find((m) => m.id === selectedUserId);
    if (selectedAgent && selectedAgent.status !== AgentStatus.ONLINE) {
      addNotification({
        type: 'error',
        message: 'This chat cannot be assigned to an inactive user. Please select an active agent',
      });
      return;
    }

    try {
      if (isAllChatsSelected) {
        await bulkAction({
          filter: chatFilters,
          excludedChatIds,
          action: BulkChatAction.ASSIGN,
          actionParams: { userId: selectedUserId, teamId: DEFAULT_TEAM },
          customFieldsData: customFieldsData ?? undefined,
        });
      } else {
        await assignMultipleChatsToUserUseCase(selectedChatIdsList, selectedUserId);
        addNotification({ type: 'success', message: 'Assigned chats successfully' });
      }
    } catch (error) {
      addNotification({
        type: 'error',
        message: (error as Error).message || 'Error while assigning user',
      });
    } finally {
      setSelectedChatIdsList([]);
      setSelectedUserId('');
      setOpen(false);
      setToggleSelectedChatIds(false);
      resetSelectionState();
    }
  };

  const handleOnClickUnassignChats = async () => {
    try {
      if (isAllChatsSelected) {
        await bulkAction({
          filter: chatFilters,
          excludedChatIds,
          action: BulkChatAction.UNASSIGN,
          customFieldsData: customFieldsData ?? undefined,
        });
      } else {
        await unassignMultipleChatsUseCase(selectedChatIdsList);
        addNotification({ type: 'success', message: 'Unassigned chats successfully' });
      }
    } catch (error) {
      addNotification({
        type: 'error',
        message: (error as Error).message || 'Error while unassigning chats',
      });
    } finally {
      setSelectedChatIdsList([]);
      setPopupState(null);
      setOpen(false);
      setToggleSelectedChatIds(false);
      resetSelectionState();
    }
  };

  const openDeletePopup = () => {
    setConfirmationModalState({
      open: true,
      message: `Are you sure you want to delete ${selectedChatIdsList.length > 1 ? 'chats' : 'chat'}?`,
      onClick: handleOnClickDeleteChats,
      onClose: () => {
        setConfirmationModalState((prev) => ({ ...prev, open: false }));
      },
      okButtonText: 'Delete',
      buttonBgColor: buttonBgColor.WARNING_BG_COLOR,
    });
    setOpen(false);
  };

  const openUnassignPopup = () => {
    setConfirmationModalState({
      open: true,
      message: `Are you sure you want to unassign ${selectedChatIdsList.length > 1 ? 'chats' : 'chat'}?`,
      onClick: handleOnClickUnassignChats,
      onClose: () => {
        setConfirmationModalState((prev) => ({ ...prev, open: false }));
      },
      okButtonText: 'Unassign',
      buttonBgColor: buttonBgColor.WARNING_BG_COLOR,
    });
    setOpen(false);
  };

  const openApplyTagsPopup = () => {
    setPopupState(PopupName.APPLY_TAG_POPUP);
    setOpen(false);
  };

  const openBroadcastListPopup = () => {
    setPopupState(PopupName.BROADCAST_LIST_POPUP);
    setOpen(false);
  };

  const openAssignElsePopup = () => {
    setPopupState(PopupName.ASSIGN_ELSE_POPUP);
    setOpen(false);
  };

  const handleCloseAssignUserModal = () => {
    setPopupState(null);
    setSelectedUserId('');
  };

  const renderModalContent = () => {
    switch (popupState) {
      case PopupName.APPLY_TAG_POPUP: {
        return (
          <Suspense fallback={<Loader size={32} secondary={LOADING} />}>
            <AddTagLazyComponent
              open={true}
              setOpen={() => setPopupState(PopupName.APPLY_TAG_POPUP)}
              setShowCreateTagModal={setCreateTagPopup}
              assignType={AddTagAssignType.MULTIPLE_CHAT}
              onClose={() => setPopupState(null)}
            />
            <CreateTagLazyComponent
              open={createTagPopup}
              setOpen={setCreateTagPopup}
              setShowAddTagModal={() => setPopupState(PopupName.APPLY_TAG_POPUP)}
            />
          </Suspense>
        );
      }

      case PopupName.BROADCAST_LIST_POPUP: {
        return (
          <QSModal open={true} onClose={() => setPopupState(null)}>
            <Box
              css={css`
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 600px;
                max-height: 500px;
                background: #fff;
                box-shadow: 24;
                overflow: hidden;
                border-radius: 4px;
                min-height: 500px;
                height: 100%;
                max-width: 100%;
              `}
            >
              <Suspense fallback={<Loader size={32} secondary={LOADING} />}>
                <MemberChannelsManagementLazyComponent
                  allowMultipleAdditions={true}
                  onClose={() => setPopupState(null)}
                />
              </Suspense>
            </Box>
          </QSModal>
        );
      }

      case PopupName.ASSIGN_ELSE_POPUP:
        return (
          <Suspense fallback={<Loader size={32} secondary={LOADING} />}>
            <AssignModalLazyComponent
              open={true}
              onClose={handleCloseAssignUserModal}
              selectedUserId={selectedUserId}
              setSelectedUserId={setSelectedUserId}
              handleOnClickAssignUser={handleOnClickAssignUser}
            />
          </Suspense>
        );
      default:
        return null;
    }
  };

  const addBroadcastSuccess = useCallback(() => {
    addNotification({ type: 'success', message: 'Successfully added chats to broadcast' });
    setSelectedChatIdsList([]);
    setToggleSelectedChatIds(false);
    resetSelectionState();
  }, [addNotification, setSelectedChatIdsList, setToggleSelectedChatIds, resetSelectionState]);

  const getSelectedCustomerIdsEvent = useCallback(async () => {
    const selectedCustomerIds = await getSelectedCustomerIds();

    postMessageToIframe(
      'members',
      JSON.stringify({
        type: 'GET_SELECTED_CUSTOMER_IDS',
        data: {
          selectedCustomerIds,
          isAllSelected: isAllChatsSelected,
          excludedIds: excludedChatIds,
          filters: buildBulkActionFilter(chatFilters, customFieldsData ?? undefined),
          // For select-all: derive from org capabilities; for individual: from actual selection
          includesInstagram: isAllChatsSelected ? orgHasInstagram : selectedChatsIncludeInstagram(),
          includesWaGroup: isAllChatsSelected ? true : selectedChatsIncludeGroupAndWaGroup(),
        },
      })
    );
  }, [
    getSelectedCustomerIds,
    isAllChatsSelected,
    excludedChatIds,
    chatFilters,
    customFieldsData,
    orgHasInstagram,
    selectedChatsIncludeInstagram,
    selectedChatsIncludeGroupAndWaGroup,
  ]);

  useEffect(() => {
    document.addEventListener('getSelectedCustomerIds', getSelectedCustomerIdsEvent);

    return () => {
      document.removeEventListener('getSelectedCustomerIds', getSelectedCustomerIdsEvent);
    };
  }, [getSelectedCustomerIds, getSelectedCustomerIdsEvent]);

  useEffect(() => {
    document.addEventListener('addToBroadcast', addBroadcastSuccess);

    return () => {
      document.removeEventListener('addToBroadcast', addBroadcastSuccess);
    };
  }, [addBroadcastSuccess]);

  return (
    <Fragment>
      {hasVisibleItems && (
        <QSTooltip title="More actions" placement="top">
          <IconButton aria-controls="menu" aria-haspopup="true" onClick={handleClick}>
            <MoreVertIcon sx={{ color: '#2d2d2d', fontSize: 20 }} />
          </IconButton>
        </QSTooltip>
      )}
      <Menu
        id="menu"
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={handleClose}
        css={css`
          & .MuiMenu-paper {
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px 0 rgba(0, 0, 0, 0.05);
          }
          & .MuiMenu-list {
            border-radius: 4px;
            border: 1px solid rgba(30, 41, 59, 0.14);
            padding: 0;
          }
        `}
      >
        {!selectedChatsIncludeGroup() && (
          <MenuItem key="add-tags" onClick={openApplyTagsPopup} css={menuItemStyles}>
            <CustomTagsIcon color="#7a7a7a" size={20} />
            Add tags
          </MenuItem>
        )}
        {(isAllChatsSelected || (!selectedChatsIncludeGroupAndWaGroup() && !selectedChatsIncludeInstagram())) &&
          canChangeChatAssignee.hasAccess && (
            <MenuItem key="assign-agent" onClick={openAssignElsePopup} css={menuItemStyles}>
              <UserIcon color="#7a7a7a" size={20} />
              Assign agent
            </MenuItem>
          )}
        {showBroadcastItem && (
          <MenuItem key="add-to-broadcast-list" onClick={openBroadcastListPopup} css={menuItemStyles}>
            <BroadcastIcon color="#7a7a7a" size={20} />
            Add to broadcast list
          </MenuItem>
        )}
        {(isAllChatsSelected || (!selectedChatsIncludeGroupAndWaGroup() && !selectedChatsIncludeInstagram())) &&
          canChangeChatAssignee.hasAccess && (
            <MenuItem key="unassign-chats" onClick={openUnassignPopup} css={menuItemStyles}>
              <UnassignAgent color="#7a7a7a" size={20} />
              Un-assign {`${isAllChatsSelected || selectedChatIdsList.length > 1 ? 'chats' : 'chat'}`}
            </MenuItem>
          )}
        {canDeleteConversation?.hasAccess && !isAllChatsSelected && (
          <MenuItem
            key="delete-chats"
            onClick={openDeletePopup}
            css={css`
              display: flex;
              height: 40px;
              padding: 6px 12px;
              align-items: center;
              gap: 6px;
              font-size: 14px;
              font-weight: 500;
              color: #ef5350;
              &:hover {
                background-color: #ef53501a;
              }
            `}
          >
            <DeleteIcon color="#EF5350" size={20} />
            Delete {`${selectedChatIdsList.length > 1 ? 'chats' : 'chat'}`}
          </MenuItem>
        )}
      </Menu>
      {confirmationModalState.open ? (
        <ConfirmationModal
          open={confirmationModalState.open}
          message={confirmationModalState.message}
          onClick={confirmationModalState.onClick}
          onClose={confirmationModalState.onClose}
          okButtonText={confirmationModalState.okButtonText}
          buttonBg={confirmationModalState.buttonBgColor}
        />
      ) : null}
      {renderModalContent()}
    </Fragment>
  );
};

export default MultiSelectChatDropdown;
